/**
 * tenantStore.js — ELYON-SHELL-FOUNDATION-1
 *
 * In watchmen-tms Phase 1, tenant state is owned by TenantContext
 * (ELYON-TENANT-ROUTING-FOUNDATION-1). This module provides derived
 * selectors consistent with the elyon-app tenantStore API so shell
 * components import from one place.
 *
 * No Zustand store needed here — TenantContext is the source of truth.
 * Zustand stores (userPrefsStore, recentsStore) layer on top.
 */
import { useTenant } from '../shell/TenantContext.jsx';

// Re-export the base hook so callers don't need to import from TenantContext directly.
export { useTenant };

/** Derived selectors — primitive (stable references, no object spread). */
export function useTenantId() {
  return useTenant().tenantId;
}

export function useTenantConfig() {
  return useTenant().tenantConfig;
}

export function useTenantLoading() {
  return useTenant().loading;
}

/**
 * Returns enabled_modules array from tenantConfig.
 * Empty array when tenantConfig is null (Phase 1 state — correct).
 * Each entry: { id: string, label?: string, icon?: string, path?: string, ... }
 */
export function useEnabledModules() {
  const { tenantConfig } = useTenant();
  return tenantConfig?.enabled_modules ?? [];
}

/**
 * Returns the tenant's environment tag.
 * Priority: explicit tenantConfig.environment → Vite MODE → 'production'.
 * Vite MODE is 'development' on local dev server, 'production' in Netlify build.
 * This ensures DEV pill shows in local dev and is hidden in production deploys.
 */
export function useTenantEnv() {
  const { tenantConfig } = useTenant();
  return tenantConfig?.environment ?? import.meta.env.MODE ?? 'production';
}

/**
 * Returns the tenant brand object (name, logoUrl, whitelabel).
 */
export function useTenantBrand() {
  const { tenantConfig } = useTenant();
  return tenantConfig?.brand ?? null;
}

/**
 * Returns AEGIS access tier for this tenant.
 * 'none' | 'tier1' | 'tier2' | 'tier3'
 */
export function useAegisAccess() {
  const { tenantConfig } = useTenant();
  return tenantConfig?.aegisAccess ?? 'none';
}
